import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { Loading, GradeBadge, EmptyState, scoreTone, FrameworkNote } from "@/components/common";
import { LessonEditorDialog } from "@/pages/LessonEditor";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { ArrowLeft, CheckCircle2, Circle, BookOpen, Sparkles, Trash2, PenLine, FileText } from "lucide-react";
import { toast } from "sonner";

export default function CourseDetail() {
  const { id } = useParams();
  const [c, setC] = useState(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const navigate = useNavigate();
  const load = useCallback(() => api.get(`/courses/${id}`).then(({ data }) => setC(data)).catch((e) => { toast.error(errMsg(e)); navigate("/catalog"); }), [id, navigate]);
  useEffect(() => { load(); }, [load]);

  if (!c) return <Loading />;
  const done = c.lessons.filter((l) => l.completed).length;
  const pct = c.lessons.length ? Math.round((done * 100) / c.lessons.length) : 0;
  const next = c.lessons.find((l) => !l.completed) || c.lessons[0];

  const del = async () => {
    try {
      await api.delete(`/courses/${id}`);
      toast.success("Course deleted");
      navigate("/catalog");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div className="max-w-[1000px]">
      <Button variant="ghost" asChild className="mb-4 -ml-3"><Link to="/catalog"><ArrowLeft className="h-4 w-4 mr-1" /> Course library</Link></Button>
      <div className="bg-white border border-border rounded-[18px] p-6 sm:p-8 fade-up">
        <div className="flex items-center gap-2 flex-wrap">
          <GradeBadge grade={c.grade_band} />
          <span className="text-sm text-muted-foreground">{c.subject}</span>
          <span className="text-sm text-muted-foreground">· by {c.author_name}</span>
        </div>
        <h1 className="mt-3 text-3xl sm:text-4xl font-display font-semibold tracking-tight" data-testid="course-title">{c.title}</h1>
        <p className="mt-2 text-muted-foreground max-w-2xl">{c.description}</p>
        <FrameworkNote item={c} className="mt-4 max-w-2xl" />
        {c.source === "library" && (
          <Button variant="link" asChild className="px-0 mt-2 h-auto text-[#0B6B6B]" data-testid="course-syllabus-link">
            <Link to={`/syllabus/${c.course_id}`}><FileText className="h-4 w-4 mr-1" />View course syllabus</Link>
          </Button>
        )}
        <div className="mt-6 flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex-1 max-w-sm">
            <div className="flex justify-between text-xs text-muted-foreground mb-1"><span>{done} of {c.lessons.length} lessons complete</span><span>{pct}%</span></div>
            <Progress value={pct} className="h-2" data-testid="course-progress" />
          </div>
          {next && <Button className="h-11" onClick={() => navigate(`/lessons/${next.lesson_id}`)} data-testid="course-start-button">{done ? "Continue course" : "Start course"}</Button>}
        </div>
        {c.can_edit && (
          <div className="mt-6 pt-6 border-t border-border flex flex-wrap gap-2">
            <Button variant="outline" className="bg-white" onClick={() => setEditorOpen(true)} data-testid="course-add-lesson-button"><PenLine className="h-4 w-4 mr-2" />Write a lesson</Button>
            <Button variant="outline" className="bg-white" asChild data-testid="course-ai-lesson-button"><Link to={`/create?course=${c.course_id}`}><Sparkles className="h-4 w-4 mr-2" />Add lesson with AI</Link></Button>
            <AlertDialog>
              <AlertDialogTrigger asChild><Button variant="ghost" className="text-destructive ml-auto" data-testid="course-delete-button"><Trash2 className="h-4 w-4 mr-2" />Delete course</Button></AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader><AlertDialogTitle>Delete this course?</AlertDialogTitle><AlertDialogDescription>All its lessons and related assignments will be removed. This can't be undone.</AlertDialogDescription></AlertDialogHeader>
                <AlertDialogFooter><AlertDialogCancel>Cancel</AlertDialogCancel><AlertDialogAction onClick={del} data-testid="course-delete-confirm">Delete</AlertDialogAction></AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        )}
      </div>

      <h2 className="mt-10 mb-4 text-sm font-display uppercase tracking-[0.14em] text-muted-foreground">Lessons</h2>
      {c.lessons.length === 0 ? (
        <EmptyState icon={BookOpen} title="No lessons yet" text={c.can_edit ? "Write a lesson or let AI draft one for this course." : "The author hasn't added lessons yet."} />
      ) : (
        <ol className="space-y-3" data-testid="course-lesson-list">
          {c.lessons.map((l, i) => (
            <li key={l.lesson_id}>
              <Link to={`/lessons/${l.lesson_id}`} className="flex items-start gap-4 bg-white border border-border rounded-[14px] p-5 lift" data-testid="course-lesson-item">
                {l.completed ? <CheckCircle2 className="h-6 w-6 text-[#1F9D55] shrink-0 mt-0.5" /> : <Circle className="h-6 w-6 text-border shrink-0 mt-0.5" />}
                <div className="flex-1 min-w-0">
                  <div className="text-xs text-muted-foreground">Lesson {i + 1} · {l.question_count} quiz questions</div>
                  <div className="font-display font-semibold text-lg">{l.title}</div>
                  <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{l.summary}</p>
                </div>
                {l.best_percent != null && <div className={`font-display font-semibold ${scoreTone(l.best_percent)}`}>{l.best_percent}%</div>}
              </Link>
            </li>
          ))}
        </ol>
      )}
      <LessonEditorDialog open={editorOpen} onOpenChange={setEditorOpen} courseId={c.course_id} onSaved={() => { setEditorOpen(false); load(); }} />
    </div>
  );
}
